import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PieChart, Pie, Cell, BarChart, Bar, Tooltip, ResponsiveContainer } from "recharts";
import { Users, UserPlus, DollarSign, Building2, Loader2 } from "lucide-react";
import { useT, useLanguage } from "../lib/LanguageContext";
import { formatMoneyIntl, intlLocaleFor } from "../lib/formatMoney";
import { currencyLabel } from "../lib/currencies";
import { PageLoadError } from "./NoAccess";
import { tenantStatusLabel } from "./tenantStatus";
import { tenantDisplayName } from "./tenantDisplay";
import CurrencyPicker from "./charts/CurrencyPicker";
import DailyRevenueChart from "./charts/DailyRevenueChart";
import { currenciesOf } from "./charts/currencies";
import DayXAxis from "./charts/DayXAxis";
import { formatCount, formatDayLong } from "./charts/chartFormat";
import { CHART_TOOLTIP, CHART_SERIES } from "./charts/chartTheme";
import { useSecondaryRead } from "./screenAccess";

// The one window of every "last N days" figure on this page (revenue, transactions, signups, churn).
const OVERVIEW_DAYS = 30;
// How many rows the Top tenants card lists for the shown currency.
const TOP_TENANTS_SHOWN = 4;

const STATUS_COLORS = { active: "#10b981", suspended: "#f43f5e", past_due: "#f97316" };
const STATUS_FALLBACK_COLOR = "#64748b";
const PLAN_COLORS = ["#3b82f6", "#f59e0b", "#10b981", "#8b5cf6", "#ec4899"];
// A non-zero amount keeps a visible bar even when it is a sliver of its currency's total.
const MIN_BAR_PERCENT = 2;

const sumCounts = (series) => (series || []).reduce((acc, p) => acc + (p.count || 0), 0);
const toCents = (amount) => Math.round((amount || 0) * 100);

export default function SuperAdminOverview() {
  const t = useT("superadmin");
  const { lang } = useLanguage();
  const [stats, setStats] = useState(null);
  const [finance, setFinance] = useState(null);
  const [series, setSeries] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  // The 30-day series is finance.read, which not every super admin holds (screenAccess.js,
  // SECONDARY_READS): without it the read is never sent and the blocks it feeds are left out.
  const canReadSeries = useSecondaryRead("financeSeries");

  useEffect(() => {
    let live = true;
    setLoadError(null);
    Promise.all([
      api.get("/super-admin/stats"),
      api.get("/super-admin/finance"),
      canReadSeries ? api.get(`/super-admin/finance/timeseries?days=${OVERVIEW_DAYS}`) : null,
    ]).then(([resStats, resFin, resSeries]) => {
      if (!live) return;
      setStats(resStats.data);
      setFinance(resFin.data);
      setSeries(resSeries ? resSeries.data : null);
    }).catch((e) => { if (live) setLoadError(e); });
    return () => { live = false; };
  }, [attempt, canReadSeries]);

  if (loadError) {
    return <PageLoadError error={loadError} onRetry={() => setAttempt((n) => n + 1)} testId="sa-overview-load-error" />;
  }

  if (!stats || !finance || (canReadSeries && !series)) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  const money = (amount, currency) => formatMoneyIntl(toCents(amount), currency, lang);

  // `/super-admin/finance` keys its maps by the stored lowercase ISO code. The headline is the
  // platform's settlement currency; every other currency that holds MRR sits beside it.
  const settlement = finance.settlement_currency;
  const mrrCurrencies = currenciesOf([finance.mrr_by_currency], settlement);
  const mrrStats = [
    { label: t("sa.ov.arrIn", { currency: currencyLabel(settlement) }), val: money(finance.arr_projection_by_currency?.[settlement] || 0, settlement) },
    ...mrrCurrencies.filter((c) => c !== settlement).map((c) => ({
      label: t("sa.ov.mrrIn", { currency: currencyLabel(c) }),
      val: money(finance.mrr_by_currency?.[c] || 0, c),
    })),
  ];

  const percent = new Intl.NumberFormat(intlLocaleFor(lang), { style: "percent", maximumFractionDigits: 0 });
  const conversion = stats.total_tenants > 0 ? percent.format(stats.paying_tenants / stats.total_tenants) : "—";

  const planNames = finance.plan_names || {};
  // currenciesOf() answers [settlement] for an empty map; a group with no plan has nothing to show.
  const planCurrencies = currenciesOf([finance.mrr_by_plan], finance.settlement_currency)
    .filter((c) => finance.mrr_by_plan?.[c]);
  const planGroups = planCurrencies.map((currency) => {
    const entries = Object.entries(finance.mrr_by_plan[currency]).sort((a, b) => b[1] - a[1]);
    const total = entries.reduce((acc, [, amount]) => acc + amount, 0);
    return {
      currency,
      rows: entries.map(([planId, amount], i) => ({
        planId,
        label: planNames[planId] || planId,
        amount,
        share: amount > 0 && total > 0 ? Math.max((amount / total) * 100, MIN_BAR_PERCENT) : 0,
        color: PLAN_COLORS[i % PLAN_COLORS.length],
      })),
    };
  });

  const statusData = Object.entries(stats.by_status || {}).map(([status, count]) => ({
    name: tenantStatusLabel(t, status),
    value: count,
    color: STATUS_COLORS[status] || STATUS_FALLBACK_COLOR,
  }));

  return (
    <div className="space-y-6 max-w-[1400px] mx-auto pb-10">
      <div>
        <h1 className="font-sans font-semibold text-5xl mb-2 text-slate-800">{t("sa.ov.title")}</h1>
        <p className="text-slate-500">{t("sa.ov.subtitle")}</p>
      </div>

      <div className={`grid grid-cols-1 md:grid-cols-2 ${series ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-5`}>
        <KpiCard
          testId="ov-kpi-tenants"
          icon={<Building2 className="w-4 h-4 text-blue-500" />} iconBg="bg-blue-100/50"
          title={t("sa.ov.totalTenants")}
          value={formatCount(stats.total_tenants, lang)}
          stats={[
            { label: tenantStatusLabel(t, "active"), val: formatCount(stats.by_status?.active || 0, lang) },
            { label: tenantStatusLabel(t, "suspended"), val: formatCount(stats.by_status?.suspended || 0, lang) },
          ]}
        />
        <KpiCard
          testId="ov-kpi-paying"
          icon={<Users className="w-4 h-4 text-emerald-500" />} iconBg="bg-emerald-100/50"
          title={t("sa.ov.payingTenants")}
          value={formatCount(stats.paying_tenants, lang)}
          stats={[
            { label: t("sa.ov.conversionRate"), val: conversion },
            { label: t("sa.ov.onTrial"), val: formatCount(stats.trialing_tenants || 0, lang) },
          ]}
        />
        {series && (
          <KpiCard
            testId="ov-kpi-signups"
            icon={<UserPlus className="w-4 h-4 text-purple-500" />} iconBg="bg-purple-100/50"
            title={t("sa.ov.newSignups30d")}
            value={formatCount(sumCounts(series.signups), lang)}
            stats={[{ label: t("sa.ov.churned30d"), val: formatCount(sumCounts(series.churn), lang) }]}
          />
        )}
        <KpiCard
          testId="ov-kpi-mrr"
          icon={<DollarSign className="w-4 h-4 text-orange-500" />} iconBg="bg-orange-100/50"
          title={t("sa.ov.mrrIn", { currency: currencyLabel(settlement) })}
          value={money(finance.mrr_by_currency?.[settlement] || 0, settlement)}
          stats={mrrStats}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {series && <RevenueCard series={series} t={t} lang={lang} />}

        <div data-testid="ov-mrr-by-plan" className={`${series ? "" : "lg:col-span-3 "}bg-white rounded-2xl p-6 border border-slate-100 shadow-sm`}>
          <h3 className="text-slate-800 font-semibold mb-6">{t("sa.ov.byPlan")}</h3>
          {planGroups.length === 0 && <div className="text-sm text-slate-400">{t("sa.ov.noMrr")}</div>}
          <div className="space-y-6">
            {planGroups.map((group) => (
              <div key={group.currency} data-testid={`ov-mrr-group-${group.currency}`} className="space-y-4">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{group.currency}</div>
                {group.rows.map((row) => (
                  <div key={row.planId}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-slate-600 font-medium text-sm">{row.label}</span>
                      <span className="text-slate-800 text-xs font-bold">{money(row.amount, group.currency)}</span>
                    </div>
                    <div className="flex gap-2 h-[12px]">
                      {row.share > 0 && (
                        <div data-share={row.share} className="rounded-full shadow-sm"
                          style={{ width: `${row.share}%`, backgroundColor: row.color }} />
                      )}
                      <div className="bg-slate-100 rounded-full flex-1" />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={`grid grid-cols-1 ${series ? "md:grid-cols-3" : "md:grid-cols-2"} gap-6`}>
        <div data-testid="ov-status" className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm flex flex-col justify-between">
          <h3 className="text-slate-800 font-semibold text-sm mb-4">{t("sa.ov.byStatus")}</h3>
          <div className="flex items-center gap-6 h-[110px] w-full">
            <div className="h-full w-24">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Tooltip {...CHART_TOOLTIP} />
                  <Pie data={statusData} innerRadius={25} outerRadius={40} paddingAngle={2} dataKey="value" nameKey="name">
                    {statusData.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-2">
              {statusData.map((s) => (
                <div key={s.name} className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                    <span className="text-xs text-slate-600">{s.name}</span>
                  </div>
                  <span className="text-sm font-bold text-slate-800">{formatCount(s.value, lang)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {series && <TransactionsCard series={series} completed={finance.completed_transactions} t={t} lang={lang} />}

        <TopTenantsCard byCurrency={finance.top_tenants_by_currency} settlement={settlement} t={t} money={money} />
      </div>
    </div>
  );
}

/**
 * One KPI card. The cards share the grid's rows (`grid-rows-subgrid` over three rows), so a
 * title that wraps in one language pushes the value and the stats of the whole row down
 * together instead of misaligning its own card. The stats sit side by side while they fit
 * and wrap onto their own line when they do not; nothing in the card is clipped, so a long
 * label or a wide amount ("1 549,00 $ CA") moves instead of being cut off.
 */
function KpiCard({ testId, icon, iconBg, title, value, stats }) {
  return (
    <div data-testid={testId} className="row-span-3 grid grid-rows-subgrid gap-y-0 bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
      <div className="flex items-start gap-3 mb-5">
        <div className={`w-9 h-9 shrink-0 rounded-xl flex items-center justify-center ${iconBg}`}>{icon}</div>
        <span className="text-slate-700 font-semibold text-[14px] self-center">{title}</span>
      </div>
      <div className="mb-6">
        <span className="text-[26px] font-bold text-slate-800 tracking-tight">{value}</span>
      </div>
      <div data-testid={`${testId}-stats`} className="flex flex-wrap gap-x-4 gap-y-3">
        {stats.map((stat) => (
          <div key={stat.label} className="flex-auto border-t border-slate-50 pt-2">
            <div className="text-slate-500 text-[11px] font-medium mb-0.5 break-words uppercase tracking-wider">{stat.label}</div>
            <div className="text-slate-700 font-bold text-sm">{stat.val}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** The best paying tenants of ONE currency at a time: amounts of different currencies are never ranked together. */
function TopTenantsCard({ byCurrency, settlement, t, money }) {
  const [pickedCurrency, setPickedCurrency] = useState(null);
  const currencies = currenciesOf([byCurrency], settlement).filter((c) => (byCurrency?.[c] || []).length > 0);
  const currency = currencies.includes(pickedCurrency) ? pickedCurrency : currencies[0];
  const rows = (byCurrency?.[currency] || []).slice(0, TOP_TENANTS_SHOWN);
  return (
    <div data-testid="ov-top-tenants" className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm flex flex-col justify-between overflow-hidden">
      <div className="flex justify-between items-start gap-3 mb-4">
        <div>
          <h3 className="text-slate-800 font-semibold text-sm">{t("sa.ov.topTenants")}</h3>
          {currency && (
            <div data-testid="ov-top-currency" className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mt-1">
              {currencyLabel(currency)}
            </div>
          )}
        </div>
        <CurrencyPicker testid="ov-top" label={t("sa.ov.currency")} currencies={currencies} value={currency} onChange={setPickedCurrency} />
      </div>
      <div className="flex-1 overflow-y-auto space-y-3 -mx-2 px-2 pb-2">
        {rows.length === 0 && <div className="text-sm text-slate-400">{t("sa.ov.noTenants")}</div>}
        {rows.map((row) => (
          <Link key={row.tenant_id} to={`/super-admin/tenants/${row.tenant_id}`} className="flex justify-between items-center hover:bg-slate-50 rounded-lg">
            <div className="flex flex-col truncate pr-2">
              <span className="text-sm font-semibold text-slate-800 truncate">{tenantDisplayName(row)}</span>
              <span className="text-[10px] uppercase text-slate-500">{row.plan_name || row.plan_id}</span>
            </div>
            <div className="text-sm font-bold text-slate-700 whitespace-nowrap">{money(row.amount, row.currency)}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}

/** Net revenue over the 30-day series, one currency at a time (the settlement one first). */
function RevenueCard({ series, t, lang }) {
  const [pickedCurrency, setPickedCurrency] = useState(null);
  const settlement = series.settlement_currency;
  const revenueCurrencies = currenciesOf([series.revenue_total_cents_by_currency], settlement);
  const revenueCurrency = revenueCurrencies.includes(pickedCurrency) ? pickedCurrency : settlement;
  const revenueTotal = series.revenue_total_cents_by_currency?.[revenueCurrency] || 0;
  return (
    <div data-testid="ov-revenue" className="lg:col-span-2 bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
      <div className="flex justify-between items-start mb-6 gap-4">
        <div>
          <h2 className="text-slate-800 font-semibold">{t("sa.ov.revenue30d")}</h2>
          <p className="text-slate-500 text-xs mt-1">{t("sa.ov.revenueHint")}</p>
          <div data-testid="ov-revenue-total" className="text-3xl font-bold text-slate-800 tracking-tight mt-3">
            {formatMoneyIntl(revenueTotal, revenueCurrency, lang)}
          </div>
        </div>
        <CurrencyPicker
          testid="ov-revenue" label={t("sa.ov.currency")}
          currencies={revenueCurrencies} value={revenueCurrency} onChange={setPickedCurrency}
        />
      </div>
      {revenueTotal === 0 ? (
        <p className="text-slate-400 text-sm py-12 text-center">{t("sa.ov.noRevenue30d")}</p>
      ) : (
        <DailyRevenueChart
          testid="ov-revenue-chart" label={t("sa.ov.revenue30d")} lang={lang} currency={revenueCurrency}
          series={series.revenue_cents_by_currency?.[revenueCurrency] || []} height={280}
        />
      )}
    </div>
  );
}

/** Captured transactions over the 30-day series; the all-time count from /finance beside it. */
function TransactionsCard({ series, completed, t, lang }) {
  return (
    <div data-testid="ov-transactions" className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm flex flex-col justify-between">
      <div>
        <h3 className="text-slate-800 font-semibold text-sm mb-4">{t("sa.ov.transactions30d")}</h3>
        <div data-testid="ov-transactions-total" className="text-[22px] font-bold text-slate-800 tracking-tight">
          {formatCount(series.transactions_total, lang)}
        </div>
        <div className="text-xs text-slate-500 font-medium mt-1">
          {t("sa.ov.txAllTime", { n: formatCount(completed, lang) })}
        </div>
      </div>
      <div className="h-[90px] w-full mt-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={series.transactions}>
            <Tooltip
              {...CHART_TOOLTIP} cursor={{ fill: "#f1f5f9" }}
              labelFormatter={(d) => formatDayLong(d, lang)}
              formatter={(v) => [formatCount(v, lang), t("sa.ov.transactions30d")]}
            />
            <Bar dataKey="count" fill={CHART_SERIES.count} radius={[3, 3, 0, 0]} />
            <DayXAxis dates={series.transactions.map((p) => p.date)} lang={lang} axisLine={false} tickLine={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
